import { useCallback, useEffect, useMemo, useState } from "react";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import Modal from "@/components/modal/Modal";
import { literals } from "@/constants/literals";
import useGarageStore from "@/store/useGarbage.store";
import { createGroup, createOrder } from "@/lib/services/order";
import {
  getImageSequence,
  uploadImagesConcurrent,
} from "@/lib/services/upload-images";
import { appToast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "@/constants/constants";
import { useLoader } from "@/store/useLoader";
import type { PhotosGroupData } from "@/types/types.d";
import DiscardOrderModal from "@/pages/capture/components/DiscardOrderModal";

type SequencedDescription = {
  lastSavedSequence: null | number;
  existFirstImage: boolean;
  description: string;
};

type SearchedDescriptions = boolean | SequencedDescription[];

const ConfirmationModals = ({
  onClose,
  viewModal,
}: {
  onClose: () => void;
  viewModal: boolean;
}) => {
  const [searchedSequences, setSearchedSequences] =
    useState<SearchedDescriptions>(false);
  const [confirmDiscardModal, setConfirmDiscardModal] = useState(false);

  const { garageData, garageGroupData } = useGarageStore();

  const navigate = useNavigate();
  const { upLoader, downLoader } = useLoader();

  const openConfirmDiscardModal = () => setConfirmDiscardModal(true);

  const closeModals = () => {
    setConfirmDiscardModal(false);
    onClose();
  };

  const closeConfirmDiscardModal = () => setConfirmDiscardModal(false);

  const getSequencedGroups = useCallback(async () => {
    const _uniqueByDescription = (groups: PhotosGroupData[]): string[] => {
      const seen = new Set<string>();
      const result: string[] = [];

      for (const group of groups) {
        const description = group?.description;
        if (typeof description !== "string") continue;

        const key = description.trim();
        if (seen.has(key)) continue;

        seen.add(key);
        result.push(description);
      }

      return result;
    };

    const descriptions = _uniqueByDescription(garageGroupData);

    const sequencesData = [];
    let getDataError = false;
    try {
      upLoader();
      for (const description of descriptions) {
        if (getDataError) break;
        else {
          try {
            const sequenceData = await getImageSequence(
              garageData.orderId as number,
              description,
            );
            sequencesData.push({
              ...sequenceData,
              description,
            });
          } catch (error) {
            if (
              error === literals.errorNotFound ||
              (error instanceof Error && error.message === literals.notFound)
            ) {
              sequencesData.push({
                lastSavedSequence: null,
                existFirstImage: false,
                description: description,
              });
            } else {
              appToast.error(literals.aProblem, literals.getNecesaryInfoError);
              getDataError = true;
            }
          }
        }
      }
    } finally {
      if (!getDataError) setSearchedSequences(sequencesData);
      else setSearchedSequences(false);
      downLoader();
    }
  }, [garageData.orderId, downLoader, upLoader, garageGroupData]);

  useEffect(() => {
    if (garageData.orderId && garageGroupData) getSequencedGroups();
    else setSearchedSequences(true);
  }, [garageData, garageGroupData, getSequencedGroups]);

  const indexedGarageGroupData = useMemo(() => {
    if (searchedSequences) {
      const addSuffix = (photo: any, suffix: number) => {
        if (photo instanceof File) {
          const cloned = new File([photo], photo.name, {
            type: photo.type,
            lastModified: photo.lastModified,
          }) as File & { suffix?: number };
          (cloned as any).suffix = suffix;
          return cloned;
        }
        return {
          ...photo,
          suffix,
        };
      };

      if (
        garageData.step !== literals.findings ||
        garageData.garage !== "TGQEC000"
      ) {
        return garageGroupData.map((garageGroup) => ({
          ...garageGroup,
          photos: garageGroup.photos.map((photo: any) => addSuffix(photo, -1)),
        }));
      }

      const counters = new Map<string, number>();

      const sequenceGroupsData =
        searchedSequences === true
          ? []
          : JSON.parse(JSON.stringify(searchedSequences));

      return garageGroupData.map((garageGroup) => {
        const description = garageGroup.description ?? "";

        const sequenceGroupIndex =
          sequenceGroupsData === true
            ? -1
            : sequenceGroupsData.findIndex(
                (group: SequencedDescription) =>
                  group.description === description,
              );

        const start = counters.get(description) ?? 0;

        const photosWithIndex = garageGroup.photos.map(
          (photo: any, index: number) => {
            if (sequenceGroupIndex === -1) {
              return addSuffix(photo, start + index);
            }
            if (
              sequenceGroupsData[sequenceGroupIndex].existFirstImage === false
            ) {
              sequenceGroupsData[sequenceGroupIndex].existFirstImage = true;
              return addSuffix(photo, 0);
            }
            if (sequenceGroupsData[sequenceGroupIndex].lastSavedSequence) {
              const nextIndex =
                sequenceGroupsData[sequenceGroupIndex].lastSavedSequence + 1;
              sequenceGroupsData[sequenceGroupIndex].lastSavedSequence =
                nextIndex;
              return addSuffix(photo, nextIndex);
            }
            return addSuffix(photo, start + index);
          },
        );
        counters.set(description, start + garageGroup.photos.length);
        return {
          ...garageGroup,
          photos: photosWithIndex,
        };
      });
    }
  }, [garageGroupData, garageData, searchedSequences]);

  const handleOnSubmit = async () => {
    if (searchedSequences && indexedGarageGroupData?.length) {
      type OrderParams = {
        area: string;
        step: string;
        sapWorkOrder: string;
        orderOut?: string;
        serialOut?: string;
        originReturn?: boolean;
        document9r1?: string;
      };
      const params: OrderParams = {
        area: garageData.garage,
        step: garageData.step,
        sapWorkOrder: garageData.orderNumber,
      };
      if (garageData.orderOut) params.orderOut = garageData.orderOut;
      if (garageData.serialOut) params.serialOut = garageData.serialOut;
      if (garageData.originReturn === true || garageData.originReturn === false)
        params.originReturn = garageData.originReturn;
      if (garageData.document9R1) params.document9r1 = garageData.document9R1;

      try {
        upLoader();
        const order = await createOrder(params);

        const groupsPromises = indexedGarageGroupData.map((group) =>
          createGroup({
            orderId: order.id,
            description: group.description,
            visible: group.visibleForClient,
          }),
        );
        const groupsList = await Promise.all(groupsPromises);

        type PhotoItem = { file: File; imageGroupId: number; route?: string };
        const photosList: PhotoItem[] = [];

        for (let i = 0; i < indexedGarageGroupData.length; i += 1) {
          const group = indexedGarageGroupData[i];
          const createdGroupId = groupsList[i].id;

          type FileWithSuffix = File & { suffix?: number };

          const makeFileWithSuffix = (
            file: File | FileWithSuffix,
            suffix?: number,
          ): FileWithSuffix => {
            const cloned = new File([file], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });
            if ("suffix" in file) (cloned as any).suffix = file.suffix;
            if ((suffix || suffix === 0) && "suffix" in file === false)
              (cloned as any).suffix = suffix;
            return cloned as FileWithSuffix;
          };

          group.photos.forEach((photo: any) => {
            const file: FileWithSuffix | undefined =
              photo?.file instanceof File
                ? makeFileWithSuffix(photo.file, photo.suffix)
                : photo instanceof File
                  ? (photo as FileWithSuffix)
                  : undefined;

            if (!file) return;
            photosList.push({
              file,
              imageGroupId: createdGroupId,
              route: photo?.route || undefined,
            });
          });
        }

        // Buckets por (imageGroupId, route)
        type Bucket = { imageGroupId: number; route?: string; files: File[] };
        const bucketsMap = new Map<string, Bucket>();
        for (const p of photosList) {
          const key = `${p.imageGroupId}|${p.route || ""}`;
          const bucket = bucketsMap.get(key);
          if (bucket) {
            bucket.files.push(p.file);
          } else {
            bucketsMap.set(key, {
              imageGroupId: p.imageGroupId,
              route: p.route,
              files: [p.file],
            });
          }
        }
        const buckets = Array.from(bucketsMap.values());

        // Concurrencia de buckets y progreso
        const BUCKET_CONCURRENCY = 3;
        const FILES_CONCURRENCY_PER_BUCKET = 6;

        const runBucketsWithConcurrency = async <T,>(
          items: Bucket[],
          worker: (b: Bucket) => Promise<T>,
          concurrency: number,
        ): Promise<T[]> => {
          const results: T[] = [];
          let index = 0;
          const runners = new Array(Math.min(concurrency, items.length))
            .fill(0)
            .map(async () => {
              while (index < items.length) {
                const current = index++;
                results[current] = await worker(items[current]);
              }
            });
          await Promise.all(runners);
          return results;
        };

        // Mapa de progreso por bucket (key: imageGroupId)
        const progressByBucket = new Map<number, number>();

        const bucketResults = await runBucketsWithConcurrency(
          buckets,
          async (bucket) => {
            // Inicializa progreso a 0 por bucket
            progressByBucket.set(bucket.imageGroupId, 0);

            const res = await uploadImagesConcurrent(
              bucket.files,
              bucket.imageGroupId,
              bucket.route,
              {
                concurrency: FILES_CONCURRENCY_PER_BUCKET,
                onProgress: (done) => {
                  // progreso por bucket
                  progressByBucket.set(bucket.imageGroupId, done);
                },
              },
            );
            return { bucket, ...res };
          },
          BUCKET_CONCURRENCY,
        );

        // Resumen y cierre
        const totalOk = bucketResults.reduce(
          (acc, r) => acc + r.ok_files.length,
          0,
        );
        const totalKo = bucketResults.reduce(
          (acc, r) => acc + r.ko_files.length,
          0,
        );

        if (totalKo > 0) throw Error;

        downLoader();
        appToast.success(
          literals.correctUpload,
          totalOk === 1
            ? literals.savedOnePicture
            : `${literals.savedPicturesPart1} ${totalOk} ${literals.images}`,
        );
        navigate(`${BASE_URL}/capture`);
      } catch (error) {
        if (error) {
          downLoader();
          appToast.error(literals.aProblem, literals.uploadNoPossible);
        }
      }
    }
  };

  return !confirmDiscardModal ? (
    <Modal title={literals.confirmation} opened={viewModal} onClose={onClose}>
      <div className="w-full grid gap-4">
        <div>
          <p className="text-sm text-ib-pt-color-gray-1000">
            {literals.confirmText}
          </p>
        </div>
        <PrimaryButton
          colour="red"
          icon="checkUnderline"
          label={literals.confirmAndExit}
          onClick={handleOnSubmit}
          disabled={!searchedSequences}
        />
        <SecondaryButton
          colour="red"
          label={literals.discardOrder}
          onClick={openConfirmDiscardModal}
        />
      </div>
    </Modal>
  ) : (
    <DiscardOrderModal
      closeDiscardModal={closeConfirmDiscardModal}
      closeModalsGroup={closeModals}
    />
  );
};

export default ConfirmationModals;
